import { useCallback, useEffect, useRef, useState } from "react";
import {
  emitNfe,
  errorMessage,
  fetchNfeState,
  refreshNfe,
} from "../services/blingService";
import { nfeProgress } from "../utils/progress";

const POLL_MS = 2000;

// Estado da NF-e de um pedido + ações de emitir / consultar SEFAZ.
// Durante a emissão consulta a etapa gravada pela API (fiscal.step) pra mostrar
// o progresso. onChange(fiscal) avisa o pai pra manter o pedido atualizado.
export function useNfe(order, onChange) {
  const [fiscal, setFiscal] = useState(order?.fiscal || null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  useEffect(() => stopPolling, []);

  useEffect(() => {
    setFiscal(order?.fiscal || null);
    setError(null);
  }, [order?._id, order?.fiscal]);

  const run = useCallback(
    async (action, track) => {
      if (!order?._id) return;
      setBusy(true);
      setError(null);
      setStep(null);
      setFinishing(false);
      if (track) {
        pollRef.current = setInterval(async () => {
          try {
            const current = await fetchNfeState(order._id);
            if (current?.step) setStep(current.step);
          } catch (_) {
            // consulta de progresso é só informativa
          }
        }, POLL_MS);
      }
      try {
        const updated = await action(order._id);
        setFinishing(true);
        const next = { ...(fiscal || {}), ...(updated || {}) };
        setFiscal(next);
        if (onChange) onChange(next);
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        stopPolling();
        setBusy(false);
        setStep(null);
      }
    },
    [order?._id, fiscal, onChange],
  );

  const emit = useCallback(() => run(emitNfe, true), [run]);
  const refresh = useCallback(() => run(refreshNfe, false), [run]);

  const progress = busy
    ? finishing
      ? { label: "Concluído", percent: 100 }
      : nfeProgress(step)
    : null;

  return { fiscal, busy, error, progress, emit, refresh };
}
